import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Quantity breaks / bundle picker.
 *
 * Renders one card per pricing tier. The selected card expands into one variant
 * row per unit in the pack, so a 2-pack can be a Small Gray and a Large Navy.
 * Adding to cart posts every row as its own line item through the AJAX cart and
 * then drives the theme's own refresh path - a `CartLinesUpdateEvent` dispatched
 * before the fetch and resolved after it, which is what `cart-drawer.js`,
 * `cart-icon.js` and `component-cart-items.js` all listen for.
 *
 * The tier prices are display prices. They are enforced at checkout by the
 * quantity-tiered automatic discounts configured on the shop, not by this file.
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} variantData - JSON payload of every variant.
 * @property {HTMLElement[]} [tiers] - The tier cards.
 * @property {HTMLInputElement[]} [radios] - The tier radios.
 * @property {HTMLButtonElement} submit - The add-to-cart button.
 * @property {HTMLElement} [submitLabel] - The label inside the button.
 * @property {HTMLElement} [error] - The inline error line.
 * @property {HTMLElement} [live] - The aria-live status region.
 *
 * @extends Component<Refs>
 */
export class OcQuantityBreaksComponent extends Component {
  requiredRefs = ['variantData', 'submit'];

  /** @type {{id: number, opts: string[], available: boolean, price: number, img: string|null}[]} */
  #variants = [];

  /** @type {AbortController | null} */
  #abort = null;

  #tier = 0;
  #busy = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetLabelTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();

    this.#tier = Number(this.dataset.selectedTier ?? 0) || 0;
    this.#variants = this.#readVariants();

    this.#syncAll();
  }

  disconnectedCallback() {
    clearTimeout(this.#resetLabelTimer);
    this.#abort?.abort();
    this.#abort = null;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback?.();

    this.#variants = this.#readVariants();

    if (!this.tiers[this.#tier]) this.#tier = 0;

    this.#syncAll();
  }

  /** @returns {HTMLElement[]} */
  get tiers() {
    return this.refs.tiers ?? [];
  }

  /** The currently selected tier card. */
  get currentTier() {
    return this.tiers[this.#tier];
  }

  /** Units in the selected pack. */
  get quantity() {
    return Math.max(1, Number(this.currentTier?.dataset.qty ?? 1) || 1);
  }

  /** How many option dimensions the product has. */
  get optionCount() {
    return Math.max(0, Number(this.dataset.optionCount ?? 0) || 0);
  }

  #readVariants() {
    try {
      const parsed = JSON.parse(this.refs.variantData.textContent || '{}');
      return Array.isArray(parsed.variants) ? parsed.variants : [];
    } catch (error) {
      console.error('[oc-quantity-breaks] variant payload is not valid JSON', error);
      return [];
    }
  }

  /* ------------------------------------------------------------------ *
   * Tier selection
   * ------------------------------------------------------------------ */

  /** @param {Event} event */
  handleTierChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.select(Number(input.value));
  }

  /**
   * Select a tier by index.
   * @param {number} index
   */
  select(index) {
    const total = this.tiers.length;
    if (!total) return;

    this.#tier = Math.min(Math.max(0, Number(index) || 0), total - 1);

    this.tiers.forEach((tier, i) => {
      tier.classList.toggle('is-selected', i === this.#tier);
    });

    const radio = this.refs.radios?.[this.#tier];
    if (radio && !radio.checked) radio.checked = true;

    this.#clearError();
    this.#syncSubmit();
  }

  /* ------------------------------------------------------------------ *
   * Option controls
   * ------------------------------------------------------------------ */

  /** @param {Event} event */
  handleSelect(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const chosen = select.options[select.selectedIndex];
    const dot = select.closest('.oc-qb__field')?.querySelector('.oc-qb__dot');
    const color = chosen?.dataset.color;

    if (dot instanceof HTMLElement && color && color !== 'none') {
      dot.style.setProperty('--oc-qb-swatch', color);
    }

    this.#syncRow(select.closest('.oc-qb__row'));
  }

  /** @param {Event} event */
  handleSwatch(event) {
    const button = event.target instanceof Element ? event.target.closest('.oc-qb__swatch') : null;
    if (!(button instanceof HTMLElement)) return;

    event.preventDefault();

    if (button.getAttribute('aria-disabled') === 'true') return;

    const group = button.closest('.oc-qb__swatches');
    if (!group) return;

    for (const sibling of group.querySelectorAll('.oc-qb__swatch')) {
      const on = sibling === button;
      sibling.classList.toggle('is-selected', on);
      sibling.setAttribute('aria-checked', on ? 'true' : 'false');
    }

    this.#syncRow(button.closest('.oc-qb__row'));
  }

  /**
   * Read the option values chosen in one row, in option order.
   * @param {Element} row
   * @returns {string[]}
   */
  #readRow(row) {
    const values = new Array(this.optionCount).fill('');

    for (const select of row.querySelectorAll('select[data-option-index]')) {
      const index = Number(select.dataset.optionIndex);
      if (Number.isInteger(index)) values[index] = select.value;
    }

    for (const group of row.querySelectorAll('.oc-qb__swatches[data-option-index]')) {
      const index = Number(group.dataset.optionIndex);
      const chosen = group.querySelector('.oc-qb__swatch.is-selected');
      if (Number.isInteger(index)) values[index] = chosen?.dataset.value ?? '';
    }

    return values;
  }

  /**
   * Find the variant matching a full set of option values.
   * @param {string[]} values
   */
  #findVariant(values) {
    if (!values.length) return this.#variants[0];

    return this.#variants.find(
      (variant) =>
        variant.opts.length === values.length && variant.opts.every((option, i) => option === values[i])
    );
  }

  /**
   * Repaint one row after a change: thumbnail, unavailable chips, submit state.
   * @param {Element | null} row
   */
  #syncRow(row) {
    if (!row) return;

    const values = this.#readRow(row);
    const variant = this.#findVariant(values);

    const thumb = row.querySelector('.oc-qb__thumb img');
    if (thumb instanceof HTMLImageElement && variant?.img) thumb.src = variant.img;

    // Grey out chips that no variant can pair with the row's other choices.
    for (const group of row.querySelectorAll('.oc-qb__swatches[data-option-index]')) {
      const index = Number(group.dataset.optionIndex);
      if (!Number.isInteger(index)) continue;

      for (const chip of group.querySelectorAll('.oc-qb__swatch')) {
        const probe = [...values];
        probe[index] = chip.dataset.value ?? '';

        const match = this.#findVariant(probe);
        const usable = Boolean(match && match.available);

        chip.setAttribute('aria-disabled', usable ? 'false' : 'true');
      }
    }

    this.#clearError();
    this.#syncSubmit();
  }

  #syncAll() {
    this.tiers.forEach((tier, i) => tier.classList.toggle('is-selected', i === this.#tier));

    for (const row of this.querySelectorAll('.oc-qb__row')) this.#syncRow(row);

    this.#syncSubmit();
  }

  /** Every variant the current tier would add, one entry per unit. */
  #currentSelection() {
    const tier = this.currentTier;
    if (!tier) return [];

    const rows = [...tier.querySelectorAll('.oc-qb__row')];

    if (!rows.length) {
      // Single-variant product: repeat the only variant for the whole pack.
      const only = this.#variants[0];
      return only ? new Array(this.quantity).fill(only) : [];
    }

    return rows.map((row) => this.#findVariant(this.#readRow(row)));
  }

  #syncSubmit() {
    const { submit, submitLabel } = this.refs;
    if (!submit) return;

    if (this.#busy) return;

    const selection = this.#currentSelection();
    const complete = selection.length > 0 && selection.every(Boolean);
    const sellable = complete && selection.every((variant) => variant?.available);

    submit.disabled = !sellable;

    if (submitLabel) {
      if (!complete) {
        submitLabel.textContent = this.dataset.unavailableText || 'Unavailable';
      } else if (!sellable) {
        submitLabel.textContent = this.dataset.soldOutText || 'Sold out';
      } else {
        submitLabel.textContent = this.dataset.addText || 'Add to cart';
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Add to cart
   * ------------------------------------------------------------------ */

  /** @param {Event} [event] */
  async handleAdd(event) {
    event?.preventDefault?.();

    if (this.#busy) return;

    const selection = this.#currentSelection();

    if (!selection.length || !selection.every((variant) => variant && variant.available)) {
      this.#syncSubmit();
      return;
    }

    const label = this.currentTier?.dataset.label || '';

    // Merge repeats so the cart shows one line with a quantity, not N lines.
    /** @type {Map<number, {id: number, quantity: number, properties?: Record<string, string>}>} */
    const merged = new Map();

    for (const variant of selection) {
      if (!variant) continue;

      const existing = merged.get(variant.id);

      if (existing) {
        existing.quantity += 1;
      } else {
        merged.set(variant.id, {
          id: variant.id,
          quantity: 1,
          ...(label ? { properties: { _pack: label } } : {}),
        });
      }
    }

    const items = [...merged.values()];

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.#setBusy(true);
    this.#clearError();

    // Dispatched before the fetch: the drawer's auto-open runs off `action`
    // synchronously, everything else subscribes to the promise.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        promise: deferred.promise,
        lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
      })
    );

    try {
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const cartUrl = window.Theme?.routes?.cart_url || '/cart';

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      }).then((result) => result.json());

      if (response.status) {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }

      const cart = await fetch(`${cartUrl}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((result) => result.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: response.sections,
          source: 'oc-quantity-breaks',
          sourceId: this.dataset.productId,
          productId: this.dataset.productId,
          didError: false,
        },
      });

      this.#announceAdded();
    } catch (error) {
      deferred.reject(error);
      this.#showError(error instanceof Error ? error.message : String(error));
    } finally {
      this.#setBusy(false);
      this.#syncSubmit();
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;

    const { submit } = this.refs;
    if (!submit) return;

    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    submit.disabled = busy;
  }

  #announceAdded() {
    const { submitLabel, live } = this.refs;
    const added = this.dataset.addedText || 'Added';

    if (live) live.textContent = added;

    if (submitLabel) {
      submitLabel.textContent = added;

      clearTimeout(this.#resetLabelTimer);
      this.#resetLabelTimer = setTimeout(() => {
        this.#syncSubmit();
        if (live) live.textContent = '';
      }, 2000);
    }
  }

  /** @param {string} message */
  #showError(message) {
    const { error, live } = this.refs;

    if (error) {
      error.textContent = message;
      error.hidden = false;
    }

    if (live) live.textContent = message;
  }

  #clearError() {
    const { error } = this.refs;

    if (error && !error.hidden) {
      error.hidden = true;
      error.textContent = '';
    }
  }
}

if (!customElements.get('oc-quantity-breaks-component')) {
  customElements.define('oc-quantity-breaks-component', OcQuantityBreaksComponent);
}